import { baseEmotionsEn, baseEmotionsRu } from '@cc/schema';
import type { SeriesChartSeries } from '../../components/charts/SeriesChart';
// E43 — the phone's rows (Q-e43g1-6 A)
import {
  RECAP_PHONE_FIELDS,
  TREND_PHONE_FIELDS,
  type RecapPhone,
  type TrendPhone,
} from '../health/checkInPhone';

/**
 * E37 · Check-in on the portal — the hub's Check-ins tab (ClientCheckInTrend, RecordCheckInInSession,
 * DeleteCheckIn from the hub) and the session form's recap (CheckInsSinceLastSession). The field lists the
 * dynamic GraphQL client sends, the types of what comes back, and the pure bits the screens share (the mood
 * series, the days a session entry can be for, an id minted in the browser).
 */
export const CHECK_IN_ENTRY_FIELDS = [
  'checkInId',
  'recordedFor',
  'occurredAt',
  'mood',
  { emotions: ['id', 'strength'] },
  'energy',
  'sleepQuality',
  'sleepHours',
  'socialConnection',
  'drivers',
  'note',
  'enteredInSession',
  'practiceId',
];

export const TREND_FIELDS = [
  'subject',
  'days',
  'daysCheckedIn',
  { entries: CHECK_IN_ENTRY_FIELDS },
  // E43 — the phone's rows for the types the client shares with this therapist
  ...TREND_PHONE_FIELDS,
];

export const RECAP_FIELDS = [
  'subject',
  'sessionId',
  'since',
  'firstSession',
  'checkIns',
  'inSessionCheckIns',
  'moodAverage',
  { moodLowest: ['checkInId', 'day', 'mood', 'note'] },
  'sleepAverageHours',
  { topDrivers: ['driver', 'count'] },
  { entries: CHECK_IN_ENTRY_FIELDS },
  // block B — THIS practice's diary card highlights since the boundary
  {
    diaryHighlights: [
      'days',
      'skillsUsedAverage',
      { highlights: ['day', 'targetId', 'name', 'urge', 'acted', 'count'] },
    ],
  },
  // E43 — the phone since the last session, for the types the client shares with this therapist
  ...RECAP_PHONE_FIELDS,
];

export const COMMAND_FIELDS = ['checkInId', 'recorded'];

export const TREND_WINDOWS = [14, 30, 90] as const;
export type TrendWindow = (typeof TREND_WINDOWS)[number];

export const MOODS = [1, 2, 3, 4, 5] as const;
export const DRIVERS = [
  'work',
  'family',
  'relationships',
  'health',
  'sleep',
  'money',
  'study',
  'weather',
  'other',
] as const;

/** The i18n key of each driver's name (`checkIn.driver.*`) — spelled out so the typed translator keeps its keys. */
export const CHECK_IN_DRIVER_LABEL_KEYS = {
  work: 'checkIn.driver.work',
  family: 'checkIn.driver.family',
  relationships: 'checkIn.driver.relationships',
  health: 'checkIn.driver.health',
  sleep: 'checkIn.driver.sleep',
  money: 'checkIn.driver.money',
  study: 'checkIn.driver.study',
  weather: 'checkIn.driver.weather',
  other: 'checkIn.driver.other',
} as const;

export type CheckInEntryRow = {
  checkInId: string;
  recordedFor: string;
  occurredAt: string;
  mood: number;
  emotions?: Array<{ id: number; strength: number }> | null;
  energy?: number | null;
  sleepQuality?: number | null;
  sleepHours?: number | null;
  socialConnection?: number | null;
  drivers?: string[] | null;
  note?: string | null;
  enteredInSession: boolean;
  practiceId?: string | null;
};

export type TrendRow = {
  subject: string;
  days: number;
  daysCheckedIn: number;
  entries: CheckInEntryRow[];
} & TrendPhone; // E43

export type RecapRow = {
  subject: string;
  sessionId?: string | null;
  since?: string | null;
  firstSession: boolean;
  checkIns: number;
  inSessionCheckIns: number;
  moodAverage?: number | null;
  moodLowest?: {
    checkInId: string;
    day: string;
    mood: number;
    note?: string | null;
  } | null;
  sleepAverageHours?: number | null;
  topDrivers: Array<{ driver: string; count: number }>;
  entries: CheckInEntryRow[];
  diaryHighlights?: {
    days: number;
    skillsUsedAverage?: number | null;
    highlights: Array<{
      day: string;
      targetId: string;
      name: string;
      urge?: number | null;
      acted?: boolean | null;
      count?: number | null;
    }>;
  } | null;
} & RecapPhone; // E43

/** One mood point per day, the day's lowest ("the views use the day's last and its lowest"), oldest first. */
export const moodSeriesOf = (
  entries: readonly CheckInEntryRow[],
): SeriesChartSeries[] => {
  const lowest = new Map<string, number>();
  for (const e of entries) {
    const cur = lowest.get(e.recordedFor);
    if (cur === undefined || e.mood < cur) lowest.set(e.recordedFor, e.mood);
  }
  return [
    {
      key: 'mood',
      label: 'mood',
      points: Array.from(lowest.entries())
        .sort((a, b) => (a[0] < b[0] ? -1 : 1))
        .map(([day, mood]) => ({ x: `${day}T12:00:00`, y: mood })),
    },
  ];
};

/** `YYYY-MM-DD` of a local date. */
export const dayOf = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

/** Today, then up to 7 days back — the days an in-session check-in can be for. */
export const recordableDays = (now: Date): string[] =>
  Array.from({ length: 8 }, (_, back) =>
    dayOf(new Date(now.getFullYear(), now.getMonth(), now.getDate() - back)),
  );

export type EmotionOption = { value: number; label: string };

export const emotionOptions = (locale: 'en' | 'ru'): EmotionOption[] =>
  (locale === 'ru' ? baseEmotionsRu : baseEmotionsEn).flatMap((g) =>
    g.children.map((c) => ({ value: c.id, label: c.name })),
  );

export const emotionLabel = (id: number, locale: 'en' | 'ru'): string =>
  emotionOptions(locale).find((o) => o.value === id)?.label ?? String(id);

/** The strength a feeling picked in session gets (the phone's "Moderate"). */
export const SESSION_EMOTION_STRENGTH = 60;

/** A check-in id minted in the browser (UUIDv7) — the fact's key, so a retried Record is the same fact. */
export const newCheckInId = (now: number = Date.now()): string => {
  const hex = (count: number) =>
    Array.from({ length: count }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join('');
  const time = now.toString(16).padStart(12, '0');
  const variant = ['8', '9', 'a', 'b'][Math.floor(Math.random() * 4)];
  return `${time.slice(0, 8)}-${time.slice(8, 12)}-7${hex(3)}-${variant}${hex(
    3,
  )}-${hex(12)}`;
};

export type SessionRecordDraft = {
  mood: number | null;
  recordedFor: string;
  energy?: number | null;
  sleepQuality?: number | null;
  sleepHours?: number | null;
  socialConnection?: number | null;
  feelings: number[];
  drivers: string[];
  note: string;
};

/** The draft as the mutation's input; null while no mood is chosen (the one required line). */
export const sessionInputOf = (
  subject: string,
  checkInId: string,
  d: SessionRecordDraft,
) =>
  d.mood === null
    ? null
    : {
        subject,
        checkInId,
        mood: d.mood,
        recordedFor: d.recordedFor,
        ...(d.feelings.length
          ? {
              emotions: d.feelings.map((id) => ({
                id,
                strength: SESSION_EMOTION_STRENGTH,
              })),
            }
          : {}),
        ...(d.energy ? { energy: d.energy } : {}),
        ...(d.sleepQuality ? { sleepQuality: d.sleepQuality } : {}),
        ...(d.sleepHours !== undefined && d.sleepHours !== null
          ? { sleepHours: d.sleepHours }
          : {}),
        ...(d.socialConnection ? { socialConnection: d.socialConnection } : {}),
        ...(d.drivers.length ? { drivers: d.drivers } : {}),
        ...(d.note.trim() ? { note: d.note.trim() } : {}),
      };
